import type { ComponentProps, ReactNode } from 'react';
import type { MDXComponents } from 'mdx/types';

/**
 * A figure-grade statistic callout. Use inside a post for a single headline
 * number with a supporting caption:
 *
 *   <Stat value="4.2%">Median gap between snapshot MRR and earned MRR…</Stat>
 */
export function Stat({ value, children }: { value: string; children: ReactNode }) {
  return (
    <div className="rv-stat">
      <span className="num">{value}</span>
      <span className="lbl">{children}</span>
    </div>
  );
}

// Markdown `>` blockquotes render as the design's pull-quote. Authors just write
// a normal blockquote and get the styled treatment for free.
function Blockquote({ className, ...rest }: ComponentProps<'blockquote'>) {
  return <blockquote {...rest} className={className ? `rv-quote ${className}` : 'rv-quote'} />;
}

/**
 * Wrap a plain Markdown list to render its items as checkbox-style rows
 * instead of the default mint bullet, for "run through this before you
 * commit" evaluation lists:
 *
 *   <Checklist>
 *   - Does pricing scale predictably as volume grows?
 *   - Can a non-engineer configure a new pricing model?
 *   </Checklist>
 *
 * Scoped to this wrapper only, so it doesn't affect every other bullet list
 * in the post.
 */
function Checklist({ children }: { children: ReactNode }) {
  return <div className="rv-checklist">{children}</div>;
}

/**
 * Components made available to every post. HTML element overrides (e.g.
 * `blockquote`) map plain Markdown onto the blog's classes; capitalised keys
 * are components an author can drop into MDX directly.
 */
export const mdxComponents: MDXComponents = {
  blockquote: Blockquote,
  Stat,
  Checklist,
};
